import React from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface ReorderableListProps<T extends { id: string | number }> {
  items: T[];
  renderItem: (item: T, dragHandle?: { attributes: any }) => React.ReactNode;
  onReorder: (newItems: T[]) => void;
  itemId: (item: T) => string | number;
  className?: string;
  dragHandle?: boolean;
}

export function ReorderableList<T extends { id: string | number }>({
  items,
  renderItem,
  onReorder,
  itemId,
  className = "",
  dragHandle = false,
}: ReorderableListProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = items.findIndex((item) => itemId(item) === active.id);
      const newIndex = items.findIndex((item) => itemId(item) === over?.id);

      const newItems = arrayMove(items, oldIndex, newIndex);
      onReorder(newItems);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items.map(itemId)} strategy={verticalListSortingStrategy}>
        <div className={className}>
          {items.map((item) => (
            <SortableItem
              key={itemId(item)}
              id={itemId(item)}
              item={item}
              renderItem={renderItem}
              dragHandle={dragHandle}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

interface SortableItemProps<T extends { id: string | number }> {
  id: string | number;
  item: T;
  renderItem: (item: T, dragHandle?: { attributes: any }) => React.ReactNode;
  dragHandle: boolean;
}

function SortableItem<T extends { id: string | number }>({
  id,
  item,
  renderItem,
  dragHandle,
}: SortableItemProps<T>) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  if (dragHandle) {
    return (
      <div style={style}>
        {renderItem(item, {
          attributes: { ...attributes, ...listeners, ref: setNodeRef },
        })}
      </div>
    );
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {renderItem(item)}
    </div>
  );
}
